import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { SKINS, SKIN_NAMES, TELEGRAM_PARAM_TO_VAR, type SkinName } from './skins';
import { getTelegramWebApp } from '../hooks/useTelegramContext';

/**
 * The four skins are two independent axes — colour scheme and platform family —
 * and the preference is stored that way rather than as a skin name. Storing the
 * axes means "Dark" keeps meaning dark when the device family changes underneath
 * it (a phone and a desktop browser share one localStorage origin only on the
 * web, but Telegram's `platform` does change between the desktop and mobile
 * clients on the same account).
 */
export const SCHEME_PREFS = ['system', 'light', 'dark'] as const;
export type SchemePref = (typeof SCHEME_PREFS)[number];

export const FAMILY_PREFS = ['system', 'ios', 'material'] as const;
export type FamilyPref = (typeof FAMILY_PREFS)[number];

export interface ThemePreference {
  scheme: SchemePref;
  family: FamilyPref;
}

export const DEFAULT_PREFERENCE: ThemePreference = { scheme: 'system', family: 'system' };

interface ThemeContextValue {
  /** The resolved skin — what is actually painted. */
  skin: SkinName;
  preference: ThemePreference;
  setPreference: (patch: Partial<ThemePreference>) => void;
  /**
   * True while both axes are `system`, i.e. nothing has been pinned. This is
   * also the only state in which Telegram's own palette is applied — see
   * the note on the apply effect below.
   */
  followsHost: boolean;
  /** `?devtheme=1` — the spec's skin switcher, kept for preview only. */
  devThemeEnabled: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  skin: 'ios-light',
  preference: DEFAULT_PREFERENCE,
  setPreference: () => {},
  followsHost: true,
  devThemeEnabled: false,
});

const STORAGE_KEY = 'manager.theme';

/** Which skin family the host platform belongs to. */
function platformFamily(): 'ios' | 'material' {
  const platform = getTelegramWebApp()?.platform;
  if (platform) {
    return platform === 'ios' || platform === 'macos' ? 'ios' : 'material';
  }
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return /iPhone|iPad|iPod|Macintosh/.test(ua) ? 'ios' : 'material';
}

function prefersDark(): boolean {
  const scheme = getTelegramWebApp()?.colorScheme;
  if (scheme) return scheme === 'dark';
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Fully automatic skin: platform family x colour scheme, both from the host. */
export function detectSkin(): SkinName {
  return resolveSkin(DEFAULT_PREFERENCE);
}

/** The preference resolved against the host, with `system` on either axis
 *  falling through to detection. */
export function resolveSkin(preference: ThemePreference): SkinName {
  const family = preference.family === 'system' ? platformFamily() : preference.family;
  const dark = preference.scheme === 'system' ? prefersDark() : preference.scheme === 'dark';
  if (family === 'ios') return dark ? 'ios-dark' : 'ios-light';
  return dark ? 'mat-dark' : 'mat-light';
}

export function applySkin(skin: SkinName) {
  const root = document.documentElement;
  const { vars, scheme, family } = SKINS[skin];
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  root.dataset.skin = skin;
  root.dataset.family = family;
  root.style.colorScheme = scheme;
}

/**
 * Inside Telegram the palette comes from the host, not from our tables. Only
 * the vars in `TELEGRAM_PARAM_TO_VAR` are overwritten — `--tg-separator`,
 * `--tg-chip-bg`, `--tg-skeleton` and the toast colours have no Telegram param
 * and stay on the skin fallback.
 */
export function applyTelegramThemeParams(params: Record<string, string> | undefined) {
  if (!params) return;
  const root = document.documentElement;
  for (const [param, cssVar] of Object.entries(TELEGRAM_PARAM_TO_VAR)) {
    const value = params[param];
    if (typeof value === 'string' && value) root.style.setProperty(cssVar, value);
  }
}

function readDevFlag(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('devtheme') === '1';
}

function isScheme(value: unknown): value is SchemePref {
  return (SCHEME_PREFS as readonly unknown[]).includes(value);
}

function isFamily(value: unknown): value is FamilyPref {
  return (FAMILY_PREFS as readonly unknown[]).includes(value);
}

/** Each axis is validated on its own, so a half-corrupt value degrades to
 *  `system` on that axis instead of discarding the whole preference. */
function readStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCE;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_PREFERENCE;
    const { scheme, family } = parsed as Record<string, unknown>;
    return {
      scheme: isScheme(scheme) ? scheme : 'system',
      family: isFamily(family) ? family : 'system',
    };
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const devThemeEnabled = useMemo(readDevFlag, []);
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [skin, setSkinState] = useState<SkinName>(() => resolveSkin(readStoredPreference()));

  const followsHost = preference.scheme === 'system' && preference.family === 'system';

  const setPreference = useCallback((patch: Partial<ThemePreference>) => {
    setPreferenceState((current) => {
      const next = { ...current, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* Private mode / blocked storage: the choice still applies for this
           session, it just will not survive a reload. */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setSkinState(resolveSkin(preference));
  }, [preference]);

  useEffect(() => {
    applySkin(skin);
    // Telegram's own palette wins over our table, but ONLY while nothing is
    // pinned. Once either axis is set explicitly, re-applying `themeParams`
    // here would immediately paint over the choice the user just made — the
    // setting would appear to do nothing inside Telegram.
    if (followsHost) applyTelegramThemeParams(getTelegramWebApp()?.themeParams);
  }, [skin, followsHost]);

  // Follow the OS while the scheme axis is on `system`. A pinned scheme must
  // not move at sunset, so the listener is not registered at all in that case.
  useEffect(() => {
    if (preference.scheme !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSkinState(resolveSkin(preference));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  // Telegram can re-theme mid-session (the user flips the app theme).
  useEffect(() => {
    const webApp = getTelegramWebApp();
    if (!webApp?.onEvent) return;
    const onThemeChanged = () => {
      setSkinState(resolveSkin(preference));
      if (followsHost) applyTelegramThemeParams(getTelegramWebApp()?.themeParams);
    };
    webApp.onEvent('themeChanged', onThemeChanged);
    return () => webApp.offEvent?.('themeChanged', onThemeChanged);
  }, [preference, followsHost]);

  const value = useMemo(
    () => ({ skin, preference, setPreference, followsHost, devThemeEnabled }),
    [skin, preference, setPreference, followsHost, devThemeEnabled],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * The spec's 4-skin switcher, behind `?devtheme=1`. Not part of the app — the
 * shipped control is Settings › Appearance. It stays because it pins all four
 * skins from one corner without walking into Settings, which is what reviewing
 * a screen against the spec actually needs. It writes the same preference, so
 * the two can never disagree.
 */
export function DevThemeSwitcher() {
  const { skin, setPreference, devThemeEnabled } = useTheme();
  if (!devThemeEnabled) return null;
  return (
    <div className="devtheme">
      {SKIN_NAMES.map((name) => (
        <button
          key={name}
          type="button"
          className={`devtheme__pill${name === skin ? ' is-active' : ''}`}
          onClick={() =>
            setPreference({ family: SKINS[name].family, scheme: SKINS[name].scheme })
          }
        >
          <span className="devtheme__swatch" style={{ background: SKINS[name].swatch }} />
          {SKINS[name].label}
        </button>
      ))}
    </div>
  );
}
